import CloseIcon from "@mui/icons-material/Close";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import React from "react";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import TimeSlot from "src/app/models/screens/availabilityTimeslot.model";
import AvailabilityCalendarScreenViewModel from "src/app/screens/availabilityCalendar/availabilityCalendar.viewmodel";
import styles from "src/app/styles/screens/availabilityCalendar.screen.style";
import { minutesToTimeString } from "src/app/utils/time.util";

const AvailabilityCalendarScreenView: React.FC = () => {
  const {
    selectedDate,
    activeTimeslots,
    onDateSelected,
    onRemoveTimeslot,
    onResetToWeeklyTemplate,
    onNavigateBack,
    isDisplayingSnackbar,
    isSnackbarError,
    setIsDisplayingSnackbar,
    snackbarMessage,
    isSelectedDateCustomized,
    isRequestingNewTimeslot,
    requestNewTimeslot,
    endTime,
    setEndTime,
    setStartTime,
    startTime,
    isMeetingSelected,
    isMeetingTyped,
    meetingDescription,
    newTagColor,
    newTagText,
    normalizeTagKey,
    onCreateTag,
    onSubmitPressed,
    selectedTagKey,
    setMeetingDescription,
    setNewTagColor,
    setNewTagText,
    tags,
    toggleTag,
    closeModal,
    isPropagateModalOpen,
    confirmPropagateAction,
    getSelectedDayOfWeek,
    isPropagatingDeletion,
    isLoadingConfirmButton,
    role,
    children,
    isLoadingChildren,
    selectedAvailabilityOwnerId,
    onSelectedAvailabilityOwnerChanged,
  } = AvailabilityCalendarScreenViewModel();

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Availability" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.mainScreenContentContainer}
      >
        <Grid container spacing={3} justifyContent="center">
          <Grid
            size={{ xl: 10, lg: 10, md: 10, sm: 8, xs: 8 }}
            sx={styles.titleContainer}
          >
            <Typography
              textTransform="math-auto"
              component="span"
              sx={styles.titleText}
            >
              Availability
            </Typography>
            <Button
              variant="outlined"
              sx={{ ml: "auto" }}
              onClick={onNavigateBack}
            >
              Template
            </Button>
          </Grid>

          {role === "Parent" && (
            <Grid size={12}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf={"end"}
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="availability-calendar-owner-select-label">
                      Calendar Owner
                    </InputLabel>
                    <Select
                      labelId="availability-calendar-owner-select-label"
                      value={selectedAvailabilityOwnerId}
                      label="Calendar Owner"
                      onChange={(event: SelectChangeEvent) =>
                        onSelectedAvailabilityOwnerChanged(event.target.value)
                      }
                      disabled={isLoadingChildren}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {children.map((child) => (
                        <MenuItem key={child._id} value={child._id}>
                          {child.firstName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            <DateCalendar value={selectedDate} onChange={onDateSelected} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {selectedDate && (
              <>
                <Box sx={styles.dateDetailsContainer}>
                  <Button
                    color="primary"
                    onClick={onResetToWeeklyTemplate}
                    variant="outlined"
                    sx={[
                      styles.newTimeslotButton,
                      {
                        visibility: isSelectedDateCustomized
                          ? "visible"
                          : "collapse",
                      },
                    ]}
                  >
                    Reset to Weekly Template
                  </Button>
                  <Button
                    color="primary"
                    onClick={requestNewTimeslot}
                    sx={styles.newTimeslotButton}
                  >
                    <MoreTimeIcon sx={styles.expandIcon} />
                  </Button>
                </Box>
                {isRequestingNewTimeslot && (
                  <Box sx={styles.newTimeslotContainer}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid size={{ xs: 12, sm: 5 }}>
                        <TextField
                          fullWidth
                          type="time"
                          label="Start Time"
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 5 }}>
                        <TextField
                          fullWidth
                          type="time"
                          label="End Time"
                          value={endTime}
                          onChange={(e) => setEndTime(e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 2 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          sx={styles.submitButton}
                          disabled={
                            !startTime ||
                            !endTime ||
                            !selectedTagKey ||
                            (isMeetingTyped && meetingDescription === "")
                          }
                          onClick={onSubmitPressed}
                        >
                          Submit
                        </Button>
                      </Grid>
                    </Grid>
                    <Divider sx={styles.newTimeslotDivider} />
                    <Box sx={styles.tagsContainer}>
                      <Typography sx={styles.tagsTitle} component="div">
                        Tags
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        sx={styles.tagsStack}
                      >
                        {tags.size === 0 ? (
                          <Typography variant="body2" sx={styles.noTagsText}>
                            No tags yet. Create one below.
                          </Typography>
                        ) : (
                          Array.from(tags.keys()).map((t: string) => {
                            return (
                              <Chip
                                key={t}
                                label={t}
                                onClick={() => toggleTag(t)}
                                variant={
                                  normalizeTagKey(t) === selectedTagKey
                                    ? "filled"
                                    : "outlined"
                                }
                                sx={{
                                  borderColor: tags.get(t),
                                  ...(normalizeTagKey(t) === selectedTagKey
                                    ? {
                                        backgroundColor: tags.get(t),
                                        color: "#fff",
                                      }
                                    : { color: tags.get(t) }),
                                }}
                              />
                            );
                          })
                        )}
                      </Stack>
                      <Typography sx={styles.newTagTitle} component="div">
                        Create new tag
                      </Typography>
                      <Grid container spacing={2} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            fullWidth
                            label="Tag text"
                            value={newTagText}
                            onChange={(e) => setNewTagText(e.target.value)}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                          <TextField
                            fullWidth
                            label="Color"
                            type="color"
                            value={newTagColor}
                            onChange={(e) => setNewTagColor(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={styles.colorPicker}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 2 }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            disabled={!normalizeTagKey(newTagText)}
                            onClick={onCreateTag}
                          >
                            Add
                          </Button>
                        </Grid>
                      </Grid>
                      {isMeetingSelected && (
                        <Box sx={styles.meetingDescriptionContainer}>
                          <TextField
                            fullWidth
                            label="Meeting description"
                            value={meetingDescription}
                            onChange={(e) =>
                              setMeetingDescription(e.target.value)
                            }
                            multiline
                            minRows={2}
                          />
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                <List>
                  {activeTimeslots.map((slot: TimeSlot) => (
                    <Box
                      key={`${slot.from}-${slot.to}`}
                      sx={styles.timeslotContainer}
                    >
                      <Box sx={styles.timeslotMainInfoContainer}>
                        <Typography>
                          {minutesToTimeString(slot.from)} -{" "}
                          {minutesToTimeString(slot.to)}
                        </Typography>
                        <Chip
                          key={slot.tag}
                          label={slot.tag}
                          variant="filled"
                          sx={[
                            styles.timeslotTag,
                            {
                              borderColor: tags.get(slot.tag),
                              backgroundColor: tags.get(slot.tag),
                            },
                          ]}
                        />
                        <IconButton
                          sx={styles.removeTimeslotButton}
                          onClick={() => onRemoveTimeslot(slot.from)}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Box>
                      <Typography>{slot.description}</Typography>
                    </Box>
                  ))}
                </List>
              </>
            )}
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>
      <Dialog
        open={isPropagateModalOpen}
        onClose={closeModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Propagation</DialogTitle>
        <DialogContent>
          <Typography>
            Should this time slot be{" "}
            {isPropagatingDeletion ? "removed from" : "added to"} all{" "}
            {getSelectedDayOfWeek()}s?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={closeModal}
            color="inherit"
            sx={styles.modalCancelButton}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmPropagateAction}
            variant="contained"
            color="primary"
            loading={isLoadingConfirmButton}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default AvailabilityCalendarScreenView;
